import { mapGetters } from 'vuex'

import { clone } from '@baserow/modules/core/utils/object'
import { notifyIf } from '@baserow/modules/core/utils/error'
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import RowService from '@baserow/modules/database/services/row'
import viewDecoration from '@baserow/modules/database/mixins/viewDecoration'
import { populateRow } from '@baserow/modules/database/store/view/grid'
import {
  filterHiddenFieldsFunction,
  filterVisibleFieldsFunction,
  sortFieldsByOrderAndIdFunction,
} from '@baserow/modules/database/utils/view'

/**
 * Lo comun a las tres vistas propias (Kanban, Calendario y Linea de tiempo).
 *
 * Las tres piden las filas de una vez y dibujan en el navegador, en vez de usar
 * la carga por ventana del core. Eso sigue siendo lo correcto —para agrupar en
 * columnas, colocar en un mes o medir un rango hacen falta todas las filas—,
 * pero arrastraba tres agujeros que este mixin cierra:
 *
 *   1. Solo se pedia UNA pagina. El endpoint pagina de 200 en 200, asi que una
 *      tabla de 250 filas se dibujaba a medias. Ahora se piden paginas hasta
 *      TOPE_TOTAL y solo se avisa en pantalla si de verdad quedan filas fuera.
 *   2. No habia metodo `refresh`. Table.vue llama `$refs.view.refresh()` al
 *      cambiar un filtro, un orden o un campo: sin el metodo, el filtro se
 *      guardaba y la vista seguia mostrando lo de antes hasta recargar.
 *   3. Al hacer clic en una tarjeta solo cambiaba la URL a /row/<id>: la ficha
 *      no se abria porque cada vista tiene que montar su propio RowEditModal,
 *      como hace la galeria del core. Aqui se monta y se conecta.
 */

// Lo que permite el endpoint por peticion (LimitOffsetPagination del plugin).
export const TOPE_PAGINA = 200
// Techo duro de filas que se dibujan. Por encima de esto la vista lo dice en
// pantalla: es preferible a colgar el navegador con una tabla enorme.
export const TOPE_TOTAL = 2000

export default {
  components: { RowEditModal },
  mixins: [viewDecoration],
  data() {
    return {
      TOPE_TOTAL,
      cargando: true,
      filas: [],
      opcionesDeCampo: {},
      hayMasFilas: false,
      showHiddenFieldsInRowModal: false,
      refrescandoFila: false,
    }
  },
  computed: {
    ...mapGetters({
      filaDelModal: 'rowModalNavigation/getRow',
    }),
    campoPrimario() {
      return this.fields.find((f) => f.primary)
    },
    /** Los campos visibles, en el orden de las opciones de la vista. */
    camposVisiblesOrdenados() {
      return this.fields
        .filter(filterVisibleFieldsFunction(this.opcionesDeCampo))
        .sort(sortFieldsByOrderAndIdFunction(this.opcionesDeCampo))
    },
    /**
     * Los campos que se pintan en la tarjeta. Cada vista puede afinarlo: el
     * Kanban, por ejemplo, quita el campo que define las columnas.
     */
    camposDeTarjeta() {
      return this.camposVisiblesOrdenados
    },
    camposOcultos() {
      return this.fields
        .filter(filterHiddenFieldsFunction(this.opcionesDeCampo))
        .sort(sortFieldsByOrderAndIdFunction(this.opcionesDeCampo))
    },
    /**
     * Color de la fila segun la decoracion configurada, para las vistas que no
     * dibujan tarjetas del core (calendario y linea de tiempo). Se usa el mismo
     * value provider que la cuadricula, asi que el color sale de la opcion de
     * seleccion o de las condiciones, sin duplicar logica.
     */
    colorDeFila() {
      const decos = this.activeDecorations.filter((d) => d.valueProviderType)
      if (!decos.length) {
        return () => ''
      }
      return (fila) => {
        for (const deco of decos) {
          const props = deco.propsFn(fila)
          if (props && props.value) {
            return props.value
          }
        }
        return ''
      }
    },
    noSePuedeEditar() {
      return (
        this.readOnly ||
        (!this.$hasPermission(
          'database.table.update_row',
          this.table,
          this.database.workspace.id
        ) &&
          !this.$hasPermission(
            'database.table.view.update_row',
            this.view,
            this.database.workspace.id
          ))
      )
    },
  },
  watch: {
    'view.id'() {
      this.traer()
    },
    filaDelModal: {
      deep: true,
      handler(fila, anterior) {
        if (!this.$refs.rowEditModal) {
          return
        }
        if (
          (anterior === null && fila !== null) ||
          (anterior && fila && anterior.id !== fila.id)
        ) {
          this.abrirFichaDeFila(fila)
        } else if (anterior !== null && fila === null) {
          // emit=false para que el modal no dispare otro `hidden` y la ruta no
          // cambie dos veces.
          this.$refs.rowEditModal.hide(false)
        }
      },
    },
  },
  mounted() {
    this.traer()
    // Un cambio hecho por otra persona llega por websocket; el tipo de vista lo
    // reemite en este bus (ver viewTypes.js). Sin esto habria que recargar.
    this.$bus.$on('mg-filas-cambiadas', this.alCambiarFilasDeFuera)
    // Entrar por la URL de una fila (/row/<id>): la pagina ya dejo la fila en el
    // store antes de montar esto, asi que el watch de abajo —que solo ve
    // cambios— nunca se entera. La galeria del core hace justo esto al final de
    // su mounted; sin ello, compartir el enlace de una tarjeta abre el tablero
    // sin la ficha, y recargar con la ficha abierta la pierde.
    if (this.filaDelModal !== null) {
      this.abrirFichaDeFila(this.filaDelModal)
    }
  },
  beforeUnmount() {
    this.$bus.$off('mg-filas-cambiadas', this.alCambiarFilasDeFuera)
  },
  methods: {
    /** Lo llama Table.vue al cambiar filtros, orden, campos o la busqueda. */
    async refresh() {
      await this.traer()
    },
    alCambiarFilasDeFuera({ tableId } = {}) {
      if (tableId === this.table.id) {
        this.traer()
      }
    },
    /**
     * Pide las filas de la vista, pagina por pagina.
     *
     * `rutaDeFilas` lo pone cada vista ('kanban-mg', 'calendario-mg' o
     * 'timeline-mg'); todas responden con el mismo formato.
     */
    async traer() {
      this.cargando = true
      try {
        const filas = []
        let opciones = this.opcionesDeCampo
        let total = 0
        for (let offset = 0; offset < TOPE_TOTAL; offset += TOPE_PAGINA) {
          const params = { limit: TOPE_PAGINA, offset }
          // Las opciones de campo no cambian entre paginas: se piden una vez.
          if (offset === 0) {
            params.include = 'field_options'
          }
          const { data } = await this.$client.get(
            `/database/views/${this.rutaDeFilas}/${this.view.id}/`,
            { params }
          )
          if (offset === 0 && data.field_options) {
            opciones = data.field_options
          }
          total = data.count || 0
          const pagina = data.results || []
          filas.push(...pagina.map((f) => populateRow(clone(f))))
          if (pagina.length < TOPE_PAGINA || filas.length >= total) {
            break
          }
        }
        this.filas = filas
        this.opcionesDeCampo = opciones
        this.hayMasFilas = total > filas.length
      } catch (e) {
        notifyIf(e, 'view')
      } finally {
        this.cargando = false
      }
    },
    /** El texto que representa a la fila cuando no hay tarjeta del core. */
    titulo(fila) {
      const p = this.campoPrimario
      const v = p ? fila[`field_${p.id}`] : null
      const texto = this.aTexto(v)
      return texto === '' ? `Fila ${fila.id}` : texto
    },
    aTexto(valor) {
      if (valor === null || valor === undefined || valor === '') {
        return ''
      }
      if (Array.isArray(valor)) {
        return valor
          .map((v) => this.aTexto(v))
          .filter(Boolean)
          .join(', ')
      }
      if (typeof valor === 'object') {
        return String(valor.value ?? valor.visible_name ?? valor.name ?? '')
      }
      return String(valor)
    },
    filaPorId(id) {
      return this.filas.find((f) => f.id === id)
    },
    abrirFila(fila) {
      this.$refs.rowEditModal.show(fila.id)
      this.$emit('selected-row', fila)
    },
    abrirFichaDeFila(fila) {
      this.$refs.rowEditModal.show(fila.id, populateRow(clone(fila)))
    },
    /**
     * Guarda un valor editado desde la ficha de la fila.
     *
     * Se escribe primero en la fila local y despues se manda: asi la tarjeta se
     * mueve de columna en el momento. Si el servidor rechaza, se deja como
     * estaba y se avisa.
     */
    async actualizarValor({ field, row, value, oldValue }) {
      const fila = this.filaPorId(row.id) || row
      const llave = `field_${field.id}`
      fila[llave] = value
      const tipo = this.$registry.get('field', field.type)
      try {
        const { data } = await RowService(this.$client).update(
          this.table.id,
          row.id,
          { [llave]: tipo.prepareValueForUpdate(field, value) }
        )
        // El servidor puede devolver mas de lo que se mando (formulas, lookups).
        Object.assign(fila, data)
      } catch (e) {
        fila[llave] = oldValue
        notifyIf(e, 'field')
      }
    },
    /**
     * Vuelve a pedir una fila al servidor. La ficha lo pide cuando se edita
     * desde otra tabla enlazada y hay que reflejar el cambio aqui.
     */
    refrescarFila(row) {
      if (this.refrescandoFila) {
        return
      }
      this.refrescandoFila = true
      this.$nextTick(async () => {
        try {
          const { data } = await RowService(this.$client).get(
            this.table.id,
            row.id
          )
          const fila = this.filaPorId(row.id)
          if (fila) {
            Object.assign(fila, data)
          }
        } catch (e) {
          notifyIf(e, 'row')
        } finally {
          this.refrescandoFila = false
        }
      })
    },
  },
}
